import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import i18n from 'i18n-js';
import { buildUrl } from 'utils';
import { debounce } from 'lodash';
import Title from 'components/Title';
import { Table } from 'components/UI';
import Layout from 'components/Layout';
import { FETCH_STATE } from 'constants';
import { useQuery } from 'hooks';
import { useHistory } from 'react-router-dom';
import OrgAbility from 'components/OrgAbility';
import { useStyles } from './BillingList.styled';
import { formatAmount, toQueryString } from 'utils';
import { UsersSelect } from 'components/AsyncFields';
import { InvoiceActions } from 'state/modules/invoice';
import { DatetimeField } from 'components/DatetimeField';

import { Grid, Paper, Container, Button } from '@material-ui/core';

const BillingList = () => {
  const classes = useStyles();
  const history = useHistory();
  const urlParams = useQuery();
  const dispatch = useDispatch();
  const isMounted = useRef(false);
  const abortController = useRef(null);
  const [params, setParams] = useState({
    page: 1,
    limit: 10,
    ...urlParams,
  });

  const [clearSelectedRows, setClearSelectedRows] = useState(false);

  const data = useSelector((state) => state.invoice.invoices);
  const pagination = useSelector((state) => state.invoice.pagination);
  const fetchingInvoices = useSelector((state) => state.invoice.fetchingInvoices);
  const fetchingProcessingRefunds = useSelector((state) => state.invoice.fetchingProcessingRefunds);

  const refetch =
    fetchingProcessingRefunds === FETCH_STATE.DONE && fetchingInvoices !== FETCH_STATE.LOADING;
  const listTitle = i18n.t('receivable.billing');

  const handleParams = (params) => {
    const data = { ...params };
    setParams(data);
    const _params = toQueryString(data);
    history.replace(buildUrl(`/billing${_params}`));
  };

  const handleSort = ({ column, sortDirection }) => {
    const newParams = {
      ...params,
      sort_field: column.selector,
      sort_desc: sortDirection === 'desc' ? true : false,
    };
    handleParams(newParams);
  };

  const handleRowClick = (row) => {
    history.push(buildUrl(`/billing/${row?.id}`));
  };

  const handlePageChange = (page) => {
    const newParams = { page: page, limit: params.limit };
    handleParams({ ...params, ...newParams });
  };

  const handlePerRowsChange = async (limit) => {
    const newParams = { page: 1, limit: limit };
    handleParams({ ...params, ...newParams });
  };

  const handleSearch = (name, value, res, extra = {}) => {
    const searchParams = {
      ...params,
      page: 1,
      ...extra,
      [name]: name === 'account_customer_id' ? res?.account_id : value,
    };

    switch (name) {
      case 'account_customer_id':
        delete searchParams.account_emitter_id;
        delete searchParams.type;
        break;
      case 'account_emitter_id':
        delete searchParams.account_customer_id;
        break;
      default:
        break;
    }

    Object.keys(searchParams).forEach(
      (key) => !searchParams[key] && searchParams[key] !== false && delete searchParams[key]
    );

    handleParams({ ...searchParams });
  };

  const fetchReceivables = useCallback(
    (params = {}) => {
      // Abort previous if any request
      abortController?.current?.abort();
      // Create new abort controller and send signal
      abortController.current = new AbortController();
      dispatch(InvoiceActions.fetchInvoices(params, { signal: abortController?.current?.signal }));
    },
    [dispatch]
  );

  const fetchReceivablesDebounced = useCallback(
    debounce((params) => fetchReceivables(params), 500),
    [fetchReceivables]
  );

  // On params change once is mounted, debounced
  useEffect(() => {
    if (isMounted.current) {
      fetchReceivablesDebounced(params);
    } else {
      isMounted.current = true;
    }
  }, [fetchReceivablesDebounced, params]);

  // Run once on mount, no debounce
  useEffect(() => {
    fetchReceivables(params);
  }, []);

  // On refetch delete/update success no debounce
  useEffect(() => {
    if (refetch) {
      setClearSelectedRows((prevState) => !prevState);
      fetchReceivables(params);
    }
  }, [fetchReceivables, refetch]);

  const columns = [
    { name: i18n.t('common.id'), selector: 'id', sortable: true, grow: 0 },
    {
      name: i18n.t('receivable.customer'),
      selector: (row) => row.customer_name,
      cell: (row) => row?.account_customer?.customer_name,
    },
    {
      name: i18n.t('receivable.reference'),
      selector: 'source_name',
      format: (row) => `${i18n.t(`receivable.reservation`)} ID#${row?.source_id}`,
      sortable: true,
      grow: 1,
    },
    {
      name: i18n.t('receivable.completed_on'),
      selector: 'completed_on',
      cell: (row) => (row.completed_on ? <DatetimeField datetime={row.completed_on} /> : '--'),
      grow: 1,
    },
    {
      name: i18n.t('receivable.modified_on'),
      selector: 'modified_time',
      cell: (row) => (row.modified_time ? <DatetimeField datetime={row.modified_time} /> : '--'),
      grow: 1,
    },
    {
      name: i18n.t('receivable.total'),
      selector: 'invoiced',
      format: (row) => `$ ${formatAmount(row.invoiced)}`,
      sortable: true,
      grow: 0,
    },
  ];

  return (
    <Layout title={listTitle}>
      <Container className={classes.container}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12}>
            <Grid
              container
              spacing={2}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Grid item style={{ flexGrow: 1 }}>
                <Title title={listTitle} />
              </Grid>
            </Grid>
          </Grid>
          <OrgAbility subject={['msServiceOrg']}>
            <Grid item xs={6} sm={2}>
              <UsersSelect
                type="users"
                margin="none"
                datacy="user-select"
                selector="account_id"
                customParams={{ is_customer: true }}
                label={i18n.t('common.filter_by_customer')}
                firstLoad={params.account_customer_id ? true : false}
                form={{
                  setFieldValue: handleSearch,
                }}
                field={{
                  name: 'account_customer_id',
                  value: params.account_customer_id || '',
                }}
              />
            </Grid>
          </OrgAbility>
          <Grid item xs={12}>
            <Paper elevation={2} className={classes.paper}>
              <Table
                data={data}
                columns={columns}
                page={params.page}
                onSort={handleSort}
                limit={params.limit}
                loading={fetchingInvoices === FETCH_STATE.LOADING}
                handleRowClicked={handleRowClick}
                handlePageChange={handlePageChange}
                defaultSortAsc={!params?.sort_desc}
                defaultSortField={params?.sort_field}
                clearSelectedRows={clearSelectedRows}
                totalRows={pagination?.totalRows || 0}
                handlePerRowsChange={handlePerRowsChange}
                data-cy="billing-table"
              />
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Layout>
  );
};

export default BillingList;
